import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AppRoutingService } from '@services/app-routing/app-routing.service';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { UtilsService } from '@services/common/utils.service';
import { AdminHTTPService } from '@services/datasources/admin-http-service';
import { AdminDataSyncService } from '@services/users/admin-data-sync.service';
import { UserInfoDto } from 'app/@types/dto/user-info-dto.model';
import { ConfigService } from 'app/app-config.service';
import { CustomValidators } from 'app/main/helpers/custom-validators';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subject, combineLatest, takeUntil } from 'rxjs';

@Component({
    selector: 'app-dedidcated-admin-form',
    templateUrl: './dedidcated-admin-form.component.html',
    styleUrls: ['./dedidcated-admin-form.component.scss'],
    providers: [ConfirmationService, MessageService],
})
export class DedidcatedAdminFormComponent implements OnInit {
    public settingsForm!: FormGroup;
    public isLoading = false;
    public createMode = false;

    get organizationFormGroup(): FormGroup {
        return this.settingsForm.get('organizationGroup') as FormGroup;
    }

    get userFormGroup(): FormGroup {
        return this.settingsForm.get('userGroup') as FormGroup;
    }

    private ngUnsubscribe = new Subject<void>();
    private dedicatedAdmin?: UserInfoDto;
    public availableLanguages = this.configService.getConfig.supportedLanguages;

    constructor(
        private translateService: CustomTranslateService,
        private formBuilder: FormBuilder,
        private adminHttpCalls: AdminHTTPService,
        private route: ActivatedRoute,
        private confirmationService: ConfirmationService,
        private messageService: MessageService,
        private appRouting: AppRoutingService,
        public adminSignals: AdminDataSyncService,
        private cdr: ChangeDetectorRef,
        private utilsService: UtilsService,
        private configService: ConfigService,
        private router: Router,
    ) {
        this.initFormControls();
    }

    ngOnInit(): void {
        // Handle initial state
        this.handleRouteState();

        // Subscribe to router events for subsequent navigation
        this.router.events.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.handleRouteState();
            }
        });
    }

    private handleRouteState(): void {
        this.isLoading = true;
        const dedicatedAdminId = this.route.snapshot.queryParams['dedicatedAdminId'];
        this.createMode = !dedicatedAdminId;

        if (!this.createMode && dedicatedAdminId) {
            this.loadAdminData(dedicatedAdminId);
        } else {
            this.settingsForm.reset();
            this.userFormGroup.enable();
            this.dedicatedAdmin = undefined;
            this.isLoading = false;
        }
    }

    private loadAdminData(adminId: string): void {
        this.adminHttpCalls
            .getUserById(adminId)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe({
                next: (adminDto) => {
                    this.settingsForm.reset();
                    this.dedicatedAdmin = adminDto;
                    this.initFormValues();
                    this.isLoading = false;
                },
            });
    }

    initFormControls(): void {
        this.settingsForm = this.formBuilder.group({
            userGroup: this.formBuilder.group({
                lastname: new FormControl(null, Validators.required),
                firstname: new FormControl(null, Validators.required),
                role: new FormControl(null),
                email: new FormControl(null, [Validators.required, CustomValidators.emailValidator]),
                phoneNumber: new FormControl(null, [Validators.required, CustomValidators.phoneNumberValidator]),
                defaultLocale: new FormControl('fr'),
            }),
        });
        if (this.adminSignals.currentSelectedDedicatedAdminId.getValue()) this.userFormGroup.disable();

        this.userFormGroup.get('email')?.valueChanges.subscribe((value) => {
            if (value) {
                const lowerCaseValue = value.toLowerCase();
                if (value !== lowerCaseValue) {
                    this.userFormGroup.get('email')?.setValue(lowerCaseValue, { emitEvent: false });
                }
            }
        });
    }
    initFormValues(): void {
        const userGroup = this.userFormGroup;

        userGroup.get('lastname')?.setValue(this.dedicatedAdmin?.lastname);
        userGroup.get('firstname')?.setValue(this.dedicatedAdmin?.firstname);
        userGroup.get('role')?.setValue(this.dedicatedAdmin?.role);
        userGroup.get('email')?.setValue(this.dedicatedAdmin?.email);
        userGroup.get('phoneNumber')?.setValue(this.dedicatedAdmin?.phone_number);
        userGroup.get('defaultLocale')?.setValue(this.dedicatedAdmin?.locale ?? 'fr');

        userGroup.markAsPristine();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    public customFormValidity(): boolean {
        return this.userFormGroup.valid && !this.userFormGroup.pristine;
    }

    onSubmit() {
        const organizationGroup = this.organizationFormGroup;
        const userGroup = this.userFormGroup;
        this.isLoading = true;

        this.adminHttpCalls
            .createDedicateAdmin({
                firstname: userGroup.get('firstname')!.value,
                lastname: userGroup.get('lastname')!.value,
                email: userGroup.get('email')!.value,
                phone_number: userGroup.get('phoneNumber')!.value.replace(/[^0-9+]/g, ''),
                locale: userGroup.get('defaultLocale')!.value ?? 'fr',
                role: userGroup.get('role')!.value,
                roles: [],
            })
            .subscribe({
                next: (result) => {
                    this.adminSignals.udpateDedicatedAdminListSubject.next(true);
                    this.adminSignals.currentSelectedDedicatedAdminId.next(result.uid ?? undefined);
                    this.adminSignals.updateDedicatedAdminNameSubject.next(result.lastname + ' ' + result.firstname);
                    this.adminSignals.getData();
                    this.appRouting.navigateToDedicatedAdminList(result.uid!);
                    this.settingsForm.markAsPristine();
                    this.isLoading = false;
                },
                error: () => {
                    this.isLoading = false;
                },
                complete: () => {
                    this.isLoading = false;
                },
            });
    }

    delete(): void {
        combineLatest([
            this.translateService.get('common.confirm_message'),
            this.translateService.get('common.confirm_title'),
            this.translateService.get('common.confirm_button'),
            this.translateService.get('common.cancel_button'),
        ]).subscribe((translations) => {
            this.confirmationService.confirm({
                message: this.utilsService.handleTranslation(translations[0]),
                header: this.utilsService.handleTranslation(translations[1]),
                acceptLabel: this.utilsService.handleTranslation(translations[2]),
                rejectLabel: this.utilsService.handleTranslation(translations[3]),
                rejectButtonStyleClass: 'dc--btn-tertiary',
                icon: 'pi pi-exclamation-triangle',
                accept: () => {
                    this.messageService.add({
                        severity: 'info',
                        summary: 'Confirmed',
                        detail: 'You have accepted',
                    });
                    if (this.dedicatedAdmin?.uid) {
                        this.adminHttpCalls
                            .deleteDedicatedAdmin(this.dedicatedAdmin.uid)
                            .pipe(takeUntil(this.ngUnsubscribe))
                            .subscribe({
                                next: (value) => {
                                    this.adminSignals.udpateDedicatedAdminListSubject.next(true);
                                    this.adminSignals.currentSelectedDedicatedAdminId.next(undefined);
                                    this.adminSignals.getData();
                                    this.appRouting.navigateToNewDedicatedUser();
                                },
                            });
                    }
                },
                reject: (type) => {},
            });
        });
    }
}
